import { ref, watch, type ComputedRef } from "vue";

export class SunCalc {
  private readonly PI = Math.PI;
  private readonly rad = this.PI / 180;
  private readonly dayMs = 1000 * 60 * 60 * 24;
  private readonly J1970 = 2440588;
  private readonly J2000 = 2451545;
  private readonly e = this.rad * 23.4397;

  private times: [number, string, string][] = [
    [-0.833, "sunrise", "sunset"],
    [-0.3, "sunriseEnd", "sunsetStart"],
    [-6, "dawn", "dusk"],
    [-12, "nauticalDawn", "nauticalDusk"],
    [-18, "nightEnd", "night"],
    [6, "goldenHourEnd", "goldenHour"],
  ];

  private toJulian(date: Date): number {
    return date.valueOf() / this.dayMs - 0.5 + this.J1970;
  }

  private fromJulian(j: number): Date {
    return new Date((j + 0.5 - this.J1970) * this.dayMs);
  }

  private toDays(date: Date): number {
    return this.toJulian(date) - this.J2000;
  }

  private rightAscension(l: number, b: number): number {
    return Math.atan2(Math.sin(l) * Math.cos(this.e) - Math.tan(b) * Math.sin(this.e), Math.cos(l));
  }

  private declination(l: number, b: number): number {
    return Math.asin(Math.sin(b) * Math.cos(this.e) + Math.cos(b) * Math.sin(this.e) * Math.sin(l));
  }

  private azimuth(H: number, phi: number, dec: number): number {
    return Math.atan2(Math.sin(H), Math.cos(H) * Math.sin(phi) - Math.tan(dec) * Math.cos(phi));
  }

  private altitude(H: number, phi: number, dec: number): number {
    return Math.asin(Math.sin(phi) * Math.sin(dec) + Math.cos(phi) * Math.cos(dec) * Math.cos(H));
  }

  private siderealTime(d: number, lw: number): number {
    return this.rad * (280.16 + 360.9856235 * d) - lw;
  }

  private astroRefraction(h: number): number {
    let innerH = h;
    if (innerH < 0) innerH = 0;
    return 0.0002967 / Math.tan(innerH + 0.00312536 / (innerH + 0.08901179));
  }

  private solarMeanAnomaly(d: number): number {
    return this.rad * (357.5291 + 0.98560028 * d);
  }

  private eclipticLongitude(M: number): number {
    const C = this.rad * (1.9148 * Math.sin(M) + 0.02 * Math.sin(2 * M) + 0.0003 * Math.sin(3 * M));
    const P = this.rad * 102.9372;
    return M + C + P + this.PI;
  }

  private sunCoords(d: number): { dec: number; ra: number } {
    const M = this.solarMeanAnomaly(d);
    const L = this.eclipticLongitude(M);
    return {
      dec: this.declination(L, 0),
      ra: this.rightAscension(L, 0),
    };
  }

  public getPosition(date: Date, lat: number, lng: number): { azimuth: number; altitude: number } {
    const lw = this.rad * -lng;
    const phi = this.rad * lat;
    const d = this.toDays(date);
    const c = this.sunCoords(d);
    const H = this.siderealTime(d, lw) - c.ra;

    return {
      azimuth: this.azimuth(H, phi, c.dec),
      altitude: this.altitude(H, phi, c.dec),
    };
  }

  public addTime(angle: number, riseName: string, setName: string): void {
    this.times.push([angle, riseName, setName]);
  }

  public getTimes(
    date: Date,
    lat: number,
    lng: number,
    height: number = 0,
  ): {
    solarNoon: Date;
    nadir: Date;
    sunrise: Date;
    sunset: Date;
    sunriseEnd: Date;
    sunsetStart: Date;
    dawn: Date;
    dusk: Date;
    nauticalDawn: Date;
    nauticalDusk: Date;
    night: Date;
    nightEnd: Date;
    goldenHour: Date;
    goldenHourEnd: Date;
  } {
    const lw = this.rad * -lng;
    const phi = this.rad * lat;
    const dh = (-2.076 * Math.sqrt(height)) / 60;
    const d = this.toDays(date);
    const n = Math.round(d - 0.0009 - lw / (2 * this.PI));
    const ds = 0.0009 + (0 + lw) / (2 * this.PI) + n;
    const M = this.solarMeanAnomaly(ds);
    const L = this.eclipticLongitude(M);
    const dec = this.declination(L, 0);
    const Jnoon = this.J2000 + ds + 0.0053 * Math.sin(M) - 0.0069 * Math.sin(2 * L);

    const result: Record<string, Date> = {
      solarNoon: this.fromJulian(Jnoon),
      nadir: this.fromJulian(Jnoon - 0.5),
    };

    for (let i = 0; i < this.times.length; i++) {
      const time = this.times[i];
      const h0 = (time[0] + dh) * this.rad;
      const w = Math.acos(
        (Math.sin(h0) - Math.sin(phi) * Math.sin(dec)) / (Math.cos(phi) * Math.cos(dec)),
      );
      const a = 0.0009 + (w + lw) / (2 * this.PI) + n;
      const Jset = this.J2000 + a + 0.0053 * Math.sin(M) - 0.0069 * Math.sin(2 * L);
      const Jrise = Jnoon - (Jset - Jnoon);
      result[time[1]] = this.fromJulian(Jrise);
      result[time[2]] = this.fromJulian(Jset);
    }

    return result as {
      solarNoon: Date;
      nadir: Date;
      sunrise: Date;
      sunset: Date;
      sunriseEnd: Date;
      sunsetStart: Date;
      dawn: Date;
      dusk: Date;
      nauticalDawn: Date;
      nauticalDusk: Date;
      night: Date;
      nightEnd: Date;
      goldenHour: Date;
      goldenHourEnd: Date;
    };
  }

  private moonCoords(d: number): { ra: number; dec: number; dist: number } {
    const L = this.rad * (218.316 + 13.176396 * d);
    const M = this.rad * (134.963 + 13.064993 * d);
    const F = this.rad * (93.272 + 13.22935 * d);

    const l = L + this.rad * 6.289 * Math.sin(M);
    const b = this.rad * 5.128 * Math.sin(F);
    const dt = 385001 - 20905 * Math.cos(M);

    return {
      ra: this.rightAscension(l, b),
      dec: this.declination(l, b),
      dist: dt,
    };
  }

  public getMoonPosition(date: Date, lat: number, lng: number) {
    const lw = this.rad * -lng;
    const phi = this.rad * lat;
    const d = this.toDays(date);
    const c = this.moonCoords(d);
    const H = this.siderealTime(d, lw) - c.ra;
    let h = this.altitude(H, phi, c.dec);
    const pa = Math.atan2(
      Math.sin(H),
      Math.tan(phi) * Math.cos(c.dec) - Math.sin(c.dec) * Math.cos(H),
    );
    h += this.astroRefraction(h);

    return {
      azimuth: this.azimuth(H, phi, c.dec),
      altitude: h,
      distance: c.dist,
      parallacticAngle: pa,
    };
  }

  public getMoonIllumination(date: Date = new Date()) {
    const d = this.toDays(date);
    const s = this.sunCoords(d);
    const m = this.moonCoords(d);
    const sdist = 149598000;

    const phi = Math.acos(
      Math.sin(s.dec) * Math.sin(m.dec) + Math.cos(s.dec) * Math.cos(m.dec) * Math.cos(s.ra - m.ra),
    );
    const inc = Math.atan2(sdist * Math.sin(phi), m.dist - sdist * Math.cos(phi));
    const angle = Math.atan2(
      Math.cos(s.dec) * Math.sin(s.ra - m.ra),
      Math.sin(s.dec) * Math.cos(m.dec) - Math.cos(s.dec) * Math.sin(m.dec) * Math.cos(s.ra - m.ra),
    );

    return {
      fraction: (1 + Math.cos(inc)) / 2,
      phase: 0.5 + (0.5 * inc * (angle < 0 ? -1 : 1)) / Math.PI,
      angle: angle,
    };
  }
  hoursLater(date: Date, h: number): Date {
    return new Date(date.valueOf() + (h * this.dayMs) / 24);
  }

  // calculations for moon rise/set times are based on http://www.stargazing.net/kepler/moonrise.html article

  public getMoonTimes(
    date: Date,
    lat: number,
    lng: number,
    inUTC: boolean,
  ): Record<string, Date | boolean> {
    const hoursLater = (dateInner: Date, h: number) =>
      new Date(dateInner.valueOf() + (h * this.dayMs) / 24);
    const t = new Date(date);
    if (inUTC) {
      t.setUTCHours(0, 0, 0, 0);
    } else {
      t.setHours(0, 0, 0, 0);
    }

    const hc = 0.133 * this.rad;
    let h0 = this.getMoonPosition(t, lat, lng).altitude - hc;
    let h1: number, h2: number, rise: number | undefined, set: number | undefined;
    let a: number,
      b: number,
      xe: number,
      ye: number = 0,
      d: number,
      roots: number,
      x1: number = 0,
      x2: number = 0,
      dx: number;

    for (let i = 1; i <= 24; i += 2) {
      h1 = this.getMoonPosition(hoursLater(t, i), lat, lng).altitude - hc;
      h2 = this.getMoonPosition(hoursLater(t, i + 1), lat, lng).altitude - hc;

      a = (h0 + h2) / 2 - h1;
      b = (h2 - h0) / 2;
      xe = -b / (2 * a);
      ye = (a * xe + b) * xe + h1;
      d = b * b - 4 * a * h1;
      roots = 0;

      if (d >= 0) {
        dx = Math.sqrt(d) / (Math.abs(a) * 2);
        x1 = xe - dx;
        x2 = xe + dx;
        if (Math.abs(x1) <= 1) roots++;
        if (Math.abs(x2) <= 1) roots++;
        if (x1 < -1) x1 = x2;
      }

      if (roots === 1) {
        if (h0 < 0) rise = i + x1;
        else set = i + x1;
      } else if (roots === 2) {
        rise = i + (ye < 0 ? x2 : x1);
        set = i + (ye < 0 ? x1 : x2);
      }

      if (rise && set) break;

      h0 = h2;
    }

    const result: Record<string, Date | boolean> = {};
    if (rise !== undefined) result.rise = hoursLater(t, rise);
    if (set !== undefined) result.set = hoursLater(t, set);
    if (rise === undefined && set === undefined) result[ye > 0 ? "alwaysUp" : "alwaysDown"] = true;

    return result;
  }
}

export function useSunAndMoon(enabled: ComputedRef<boolean>) {
  const sunCalc = new SunCalc();

  function getMoonPhaseEmoji(phase: number): string {
    if (phase < 0.03 || phase > 0.97) return "🌑"; // New Moon
    if (phase < 0.22) return "🌒"; // Waxing Crescent
    if (phase < 0.28) return "🌓"; // First Quarter
    if (phase < 0.47) return "🌔"; // Waxing Gibbous
    if (phase < 0.53) return "🌕"; // Full Moon
    if (phase < 0.72) return "🌖"; // Waning Gibbous
    if (phase < 0.78) return "🌗"; // Last Quarter
    return "🌘"; // Waning Crescent
  }

  const data = ref<{ icon: string; times: ReturnType<typeof sunCalc.getTimes> }>();

  function updateIcon(lat: number, lng: number) {
    const now = new Date();
    const times = sunCalc.getTimes(now, lat, lng);
    const isDay = now >= times.sunrise && now < times.sunset;

    let icon: string;
    if (isDay) {
      icon = "☀️";
    } else {
      const moon = sunCalc.getMoonIllumination(now);
      icon = getMoonPhaseEmoji(moon.phase);
    }

    data.value = {
      icon,
      times,
    };
  }

  let interval: number;

  function enable() {
    if (interval) clearInterval(interval);
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (pos) => {
          updateIcon(pos.coords.latitude, pos.coords.longitude);
          interval = setInterval(
            () => updateIcon(pos.coords.latitude, pos.coords.longitude),
            60 * 1000,
          );
        },
        () => {
          // Fallback: Berlin
          updateIcon(52.52, 13.405);
          interval = setInterval(() => updateIcon(52.52, 13.405), 60 * 1000);
        },
      );
    } else {
      updateIcon(52.52, 13.405); // fallback
    }
  }

  watch(
    enabled,
    (newValue) => {
      if (newValue) {
        enable();
      } else {
        stop();
      }
    },
    { immediate: true },
  );

  function stop() {
    if (interval) clearInterval(interval);
  }

  return { stop, data };
}
